import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  Alert, Button, Col, Form, Row, Select, Tooltip,
} from 'antd';
import { CheckOutlined, CopyOutlined } from '@ant-design/icons';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import parse from 'html-react-parser';
import {
  Consent, FormInput, FormItem, FormSelect, StyledCheckbox,
} from '../../../components';
import { ScrollableValue, Styles } from './TelematicAccount.styled';
import ApiService from '../../../services/ApiService';
import UrlService from '../../../services/UrlService';
import KeyValueList from './KeyValueList';

const MIN_VAT_NUMBER_LENGTH = 5;

export default function TelematicAccountDetailsSection({
  onSelectChange,
  selectedProvider,
  isDetailsDisabled,
  creationView,
  previousAttempt,
  onFillFromPreviousAttempt,
  onDismissPreviousAttempt,
  generatedCredentials,
}) {
  const { t } = useTranslation();
  const form = Form.useFormInstance();
  const [extraTexts, setExtraText] = useState({});
  const [borderColor, setBorderColor] = useState(undefined);
  const [copiedField, setCopiedField] = useState(null);

  const handleCopy = (fieldName, value) => {
    if (value === undefined || value === null || value === '') return;
    navigator.clipboard.writeText(String(value));
    setCopiedField(fieldName);
    setTimeout(() => {
      setCopiedField((current) => (current === fieldName ? null : current));
    }, 2000);
  };

  const isEmptyValue = (value) => {
    if (value === undefined || value === null || value === '') return true;
    if (Array.isArray(value) && value.length === 0) return true;
    if (typeof value === 'object' && Object.keys(value).length === 0) return true;
    return false;
  };

  // Autocomplete fields hold values generated by the backend (e.g. PUSH-mode
  // credentials). Display-only, rendered inside a standard FormItem so the
  // label/spacing matches the rest of the form. Fixed-height single-line slot
  // styled like a disabled input, value scrolls horizontally when it overflows,
  // copy icon stays pinned on the right and is always visible.
  const renderAutocompleteDisplay = ({ fieldName, i18nKey }, wide = true) => {
    const value = generatedCredentials?.[fieldName] ?? form?.getFieldValue(fieldName);
    if (isEmptyValue(value)) return null;
    const sizeStyle = wide ? Styles.wide : Styles.narrow;
    const label = t(`TELEMATICS.ACCOUNT.CREATE.LABELS.${i18nKey.toUpperCase()}`);
    return (
      <FormItem
        key={fieldName}
        label={label}
        style={sizeStyle}
      >
        <div
          style={{
            ...sizeStyle,
            position: 'relative',
            height: 32,
            border: '1px solid #d9d9d9',
            borderRadius: 2,
            background: '#f5f5f5',
            color: 'rgba(0, 0, 0, 0.65)',
            overflow: 'hidden',
            boxSizing: 'border-box',
          }}
        >
          {/* Scrollable value area — bounded on the right so its scroll
              container doesn't extend under the copy button. Native scrollbar
              is hidden (styled-component) so short 32px rows don't get their
              value squeezed by Chrome/Edge's classic scrollbar chrome. */}
          <ScrollableValue>
            {value}
          </ScrollableValue>
          {/* Copy button pinned to the right — its own 40x32 slot, no overlap
              with the scroll container, so the entire rectangle is the click
              target. */}
          <Tooltip
            title={t(copiedField === fieldName
              ? 'COMMON.COPIED_TO_CLIPBOARD'
              : 'COMMON.COPY_TO_CLIPBOARD')}
          >
            <div
              role="button"
              tabIndex={0}
              aria-label={t('COMMON.COPY_TO_CLIPBOARD')}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleCopy(fieldName, value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleCopy(fieldName, value);
                }
              }}
              style={{
                position: 'absolute',
                top: 0,
                right: 0,
                bottom: 0,
                width: 40,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                background: '#f5f5f5',
                color: copiedField === fieldName ? '#52c41a' : 'inherit',
                fontSize: 16,
                zIndex: 2,
              }}
            >
              {copiedField === fieldName ? <CheckOutlined /> : <CopyOutlined />}
            </div>
          </Tooltip>
        </div>
      </FormItem>
    );
  };

  const validateCheckbox = (_, isChecked) => {
    setBorderColor(isChecked ? undefined : 'red');
    return isChecked ? Promise.resolve() : Promise.reject();
  };

  const updateExtraText = (field, value) => {
    const newValue = {};
    newValue[field] = value;
    setExtraText({ ...extraTexts, ...newValue });
  };

  const updateExtraTextForVatNumber = (inputValue, fieldName) => {
    if (inputValue && inputValue.length >= MIN_VAT_NUMBER_LENGTH) {
      ApiService.defaultInstance.get(UrlService.findCompany(), {
        params: {
          taxCountryCode: inputValue.substring(0, 2),
          taxNo: inputValue.substring(2, inputValue.length),
          skipValidation: true,
        },
      }).then((response) => {
        if (response && response.data && response.data.companyName) {
          updateExtraText(fieldName, response.data.companyName);
        } else {
          updateExtraText(fieldName, t('TELEMATICS.ACCOUNT.CREATE.ERRORS.COMPANY_WITH_VAT_NOT_FOUND'));
        }
      }).catch(() => {
        updateExtraText(fieldName, t('TELEMATICS.ACCOUNT.CREATE.ERRORS.COMPANY_WITH_VAT_NOT_FOUND'));
      });
    }
  };

  const renderSelect = ({
    fieldName, i18nKey, optional, options,
  }, wide = true, disabled = true) => (
    <FormItem
      label={t(`TELEMATICS.ACCOUNT.CREATE.LABELS.${i18nKey.toUpperCase()}`)}
      name={fieldName}
      rules={!disabled ? [
        { required: !optional, message: t('TELEMATICS.ACCOUNT.CREATE.ERRORS.REQUIRED') },
      ] : []}
      style={wide ? Styles.wide : Styles.narrow}
    >
      <FormSelect
        placeholder={t(`TELEMATICS.ACCOUNT.CREATE.PLACEHOLDERS.${i18nKey.toUpperCase()}`)}
        style={wide ? Styles.wide : Styles.narrow}
        disabled={disabled}
        onChange={onSelectChange}
      >
        {options.map((option) => (
          <Select.Option key={option.value} value={option.value}>
            {/* eslint-disable-next-line no-nested-ternary */}
            {option.displayNameKey ? t(`TELEMATICS.ACCOUNT.CREATE.LABELS.${option.displayNameKey.toUpperCase()}`)
              : option.name ? option.name
                : option.value}
          </Select.Option>
        ))}
      </FormSelect>
    </FormItem>
  );

  const renderTextInput = ({
    fieldName, i18nKey, optional,
  }, wide = true, disabled = true) => (
    <FormItem
      label={t(`TELEMATICS.ACCOUNT.CREATE.LABELS.${i18nKey.toUpperCase()}`)}
      name={fieldName}
      rules={!disabled ? [
        { required: !optional, message: t('TELEMATICS.ACCOUNT.CREATE.ERRORS.REQUIRED') },
        { type: 'string', whitespace: true, message: t('TELEMATICS.ACCOUNT.CREATE.ERRORS.BLANK') },
      ] : []}
      style={wide ? Styles.wide : Styles.narrow}
      extra={<small>{extraTexts[fieldName]}</small>}
    >
      <FormInput
        disabled={disabled}
        style={wide ? Styles.wide : Styles.narrow}
        placeholder={t(`TELEMATICS.ACCOUNT.CREATE.PLACEHOLDERS.${i18nKey.toUpperCase()}`)}
        onChange={AwesomeDebouncePromise((e) => {
          const inputValue = e.target.value;
          if (fieldName === 'vat_number') {
            updateExtraTextForVatNumber(inputValue, fieldName);
          }
        }, 1000)}
      />
    </FormItem>
  );

  const renderPasswordInput = ({
    fieldName, i18nKey, optional,
  }, wide = true, disabled = true) => (
    <FormItem
      label={t(`TELEMATICS.ACCOUNT.CREATE.LABELS.${i18nKey.toUpperCase()}`)}
      name={fieldName}
      rules={!disabled ? [
        { required: !optional, message: t('TELEMATICS.ACCOUNT.CREATE.ERRORS.REQUIRED') },
        { type: 'string', whitespace: true, message: t('TELEMATICS.ACCOUNT.CREATE.ERRORS.BLANK') },
      ] : []}
      style={wide ? Styles.wide : Styles.narrow}
    >
      <FormInput.Password
        disabled={disabled}
        style={wide ? Styles.wide : Styles.narrow}
        placeholder={t(`TELEMATICS.ACCOUNT.CREATE.PLACEHOLDERS.${i18nKey.toUpperCase()}`)}
      />
    </FormItem>
  );

  const renderTextArea = ({
    fieldName, i18nKey, optional,
  }, wide = true, disabled = true) => (
    <FormItem
      label={t(`TELEMATICS.ACCOUNT.CREATE.LABELS.${i18nKey.toUpperCase()}`)}
      name={fieldName}
      rules={!disabled ? [
        { required: !optional, message: t('TELEMATICS.ACCOUNT.CREATE.ERRORS.REQUIRED') },
        { type: 'string', whitespace: true, message: t('TELEMATICS.ACCOUNT.CREATE.ERRORS.BLANK') },
      ] : []}
      style={wide ? Styles.wide : Styles.narrow}
    >
      <FormInput.TextArea
        disabled={disabled}
        style={wide ? Styles.wideWithMinHeight : Styles.narrow}
        placeholder={t(`TELEMATICS.ACCOUNT.CREATE.PLACEHOLDERS.${i18nKey.toUpperCase()}`)}
      />
    </FormItem>
  );

  const renderCheckbox = ({
    fieldName, optional, notUpdatable, value,
  }, wide = true, disabled = true) => (
    <FormItem
      name={fieldName}
      valuePropName="checked"
      rules={!disabled ? [
        { required: !optional, message: () => null }, { validator: validateCheckbox },
      ] : []}
      style={wide ? Styles.wide : Styles.narrow}
    >
      <StyledCheckbox
        disabled={disabled}
        bordercolor={borderColor}
        defaultChecked={!creationView && notUpdatable}
      >
        <Consent>
          {parse(value)}
          {!optional
            ? (
              <span style={{ color: 'red' }}>
                *
              </span>
            ) : <></>}
        </Consent>
      </StyledCheckbox>
    </FormItem>
  );

  const renderInput = (attr, wide = true) => {
    // Autocomplete fields carry backend-generated read-only values (e.g. PUSH
    // credentials). Render them as a disabled-input-styled slot with a copy
    // affordance so they visually match the rest of the form.
    if (attr.autocomplete !== null) {
      return renderAutocompleteDisplay(attr, wide);
    }
    const disabled = isDetailsDisabled || (!creationView && attr.notUpdatable);
    switch (attr.type) {
      case 'DROPDOWN':
      case 'LONG_DROPDOWN':
        return renderSelect(attr, wide, disabled);
      case 'PASSWORD':
      case 'LONG_PASSWORD':
        return renderPasswordInput(attr, wide, disabled);
      case 'LONG_TEXT':
        return renderTextArea(attr, wide, disabled);
      case 'CHECKBOX':
      case 'CHECKBOX_CONSENT':
        return renderCheckbox(attr, wide, disabled);
      case 'KEY_VALUE_LIST':
        return (
          <KeyValueList
            fieldName={attr.fieldName}
            fieldsConfig={attr.keyValueListOptions}
          />
        );
      default:
        return renderTextInput(attr, wide, disabled);
    }
  };

  const renderForm = () => {
    const rows = [];
    const normalFields = [];
    let keyValueField = null;

    // Separate normal fields and key_value_list. Autocomplete fields flow
    // through the normal row layout — they render as FormItem-styled read-only
    // slots so they visually match the rest of the form.
    for (let i = 0; i < selectedProvider.fields.length; i += 1) {
      const f = selectedProvider.fields[i];
      if (f.autocomplete !== null) {
        const value = generatedCredentials?.[f.fieldName] ?? form?.getFieldValue(f.fieldName);
        if (!isEmptyValue(value)) {
          normalFields.push(f);
        }
      } else if (f.type === 'KEY_VALUE_LIST') {
        keyValueField = f;
      } else {
        normalFields.push(f);
      }
    }

    for (let i = 0; i < normalFields.length;) {
      const f1 = normalFields[i];
      const f2 = i + 1 < normalFields.length ? normalFields[i + 1] : undefined;
      if (f1.type.startsWith('LONG_') || f1.type.startsWith('CHECKBOX')) {
        rows.push([f1]);
        i += 1;
      } else if (f2 && !f2.type.startsWith('LONG_')) {
        rows.push([f1, f2]);
        i += 2;
      } else {
        rows.push([f1]);
        i += 1;
      }
    }

    return (
      <>
        {rows.map((pair) => (
          <Row gutter={100} key={pair.map((f) => f.fieldName).join('-')}>
            <Col span={pair.length > 1 ? 10 : 20}>
              {pair[0] && renderInput(pair[0], pair.length === 1)}
            </Col>
            {pair.length > 1 && (
            <Col span={10}>
              {pair[1] && renderInput(pair[1], false)}
            </Col>
            )}
          </Row>
        ))}

        {keyValueField && (
        <Row>
          <Col span={20}>
            {renderInput(keyValueField, true)}
          </Col>
        </Row>
        )}
      </>
    );
  };

  return (
    <>
      {previousAttempt && (
        <Alert
          type="info"
          showIcon
          closable
          onClose={onDismissPreviousAttempt}
          style={{ ...Styles.wide, marginBottom: 16 }}
          message={<b>{t('TELEMATICS.ACCOUNT.CREATE.SECTIONS.ACCOUNT.PREVIOUS_ATTEMPT_TITLE')}</b>}
          description={(
            <>
              <p style={{ marginBottom: 8 }}>
                {t('TELEMATICS.ACCOUNT.CREATE.SECTIONS.ACCOUNT.PREVIOUS_ATTEMPT_DESCRIPTION')}
              </p>
              <Button size="small" onClick={onFillFromPreviousAttempt}>
                {t('TELEMATICS.ACCOUNT.CREATE.SECTIONS.ACCOUNT.PREVIOUS_ATTEMPT_FILL_BUTTON')}
              </Button>
            </>
                    )}
        />
      )}
      {selectedProvider && (renderForm())}
    </>
  );
}

TelematicAccountDetailsSection.propTypes = {
  onSelectChange: PropTypes.func.isRequired,
  selectedProvider: PropTypes.shape({
    providerId: PropTypes.string,
    displayName: PropTypes.string,
    fields: PropTypes.arrayOf(PropTypes.shape({
      fieldName: PropTypes.string,
      i18nKey: PropTypes.string,
      type: PropTypes.string,
      keyName: PropTypes.string,
      valueName: PropTypes.string,
      autocomplete: PropTypes.string,
    })),
    assistance: PropTypes.shape({
      noAssistance: PropTypes.bool,
    }),
  }),
  isDetailsDisabled: PropTypes.bool,
  creationView: PropTypes.bool,
  previousAttempt: PropTypes.shape({
    companyId: PropTypes.string,
    type: PropTypes.string,
    // eslint-disable-next-line react/forbid-prop-types
    formData: PropTypes.object,
  }),
  onFillFromPreviousAttempt: PropTypes.func,
  onDismissPreviousAttempt: PropTypes.func,
  // eslint-disable-next-line react/forbid-prop-types
  generatedCredentials: PropTypes.object,
};

TelematicAccountDetailsSection.defaultProps = {
  selectedProvider: undefined,
  isDetailsDisabled: false,
  creationView: true,
  previousAttempt: null,
  onFillFromPreviousAttempt: undefined,
  onDismissPreviousAttempt: undefined,
  generatedCredentials: undefined,
};
